// src/context/AppStateContext.jsx
//
// STATE MANAGEMENT STRATEGY
// ─────────────────────────
// Context (this file): State shared across multiple hooks and components.
//   - Graph data (rawData, edgeList, serviceNodes, serviceEdges)
//   - Modal/menu open/close state (accessed by ModalsContainer + handler hooks)
//   - Loading flags (read by App.js UI, written by useDataLoader)
//   - Error messages (read by App.js, written by any hook)
//
// Local useState (in App.js): UI-only state scoped to the App component tree.
//   - Selection state (selectedNode, selectedTextNode, lastClickedNode)
//   - Panel visibility (sidebarOpen, controlsPanelVisible, editBackground)
//   - Layout parameters (verticalSpacing, horizontalSpacing, edgeType, nodeMode)
//   - Search terms, React Flow instance ref, etc.
//
// This split avoids putting everything in Context (which would cause excessive
// re-renders) while keeping shared data accessible to extracted hooks.

import React, { createContext, useContext, useReducer, useMemo } from 'react';

const AppStateContext = createContext();

// ── INITIAL STATE ─────────────────────────────────────────────────────────
const initialState = {
  rawData: [],
  edgeList: [],
  serviceNodes: [],
  serviceEdges: [],

  // Modal states
  deleteConfirmationModal: { open: false, node: null },
  edgeDeleteConfirmationModal: { open: false, edge: null },
  edgeTextModal: { open: false, edgeId: null, currentText: "" },
  // What a connection MEANS (requires / hosted on / …), not how it is drawn
  edgeTypeModal: { open: false, edge: null, busy: false, error: null },
  edgeTypePalette: [],

  // Context menu states
  edgeContext: { open: false, x: 0, y: 0, edge: null },
  contextMenu: { open: false, x: 0, y: 0, node: null },

  // Add Image Modal
  addImageModalOpen: false,

  // Loading and error states
  recoveryStatsLoading: false,
  initialLoading: true,
  refreshing: false,
  paletteLoading: false,
  mainPaletteLoading: false,
  errorMessage: null,
};

// ── MAP ACTION TYPES TO STATE KEYS ────────────────────────────────────────
const actionToKey = {
  SET_RAW_DATA: 'rawData',
  UPDATE_RAW_DATA: 'rawData',
  SET_EDGE_LIST: 'edgeList',
  UPDATE_EDGE_LIST: 'edgeList',
  SET_SERVICE_NODES: 'serviceNodes',
  UPDATE_SERVICE_NODES: 'serviceNodes',
  SET_SERVICE_EDGES: 'serviceEdges',
  UPDATE_SERVICE_EDGES: 'serviceEdges',
  SET_DELETE_CONFIRMATION_MODAL: 'deleteConfirmationModal',
  SET_EDGE_DELETE_CONFIRMATION_MODAL: 'edgeDeleteConfirmationModal',
  SET_EDGE_TEXT_MODAL: 'edgeTextModal',
  SET_EDGE_TYPE_MODAL: 'edgeTypeModal',
  SET_EDGE_TYPE_PALETTE: 'edgeTypePalette',
  SET_EDGE_CONTEXT: 'edgeContext',
  SET_CONTEXT_MENU: 'contextMenu',
  SET_RECOVERY_STATS_LOADING: 'recoveryStatsLoading',
  SET_INITIAL_LOADING: 'initialLoading',
  SET_REFRESHING: 'refreshing',
  SET_PALETTE_LOADING: 'paletteLoading',
  SET_MAIN_PALETTE_LOADING: 'mainPaletteLoading',
  SET_ADD_IMAGE_MODAL: 'addImageModalOpen',
  SET_ERROR_MESSAGE: 'errorMessage',
};

// ── REDUCER ───────────────────────────────────────────────────────────────
function appReducer(state, action) {
  const key = actionToKey[action.type];
  if (!key) {
    return state;
  }

  // Support updater functions (e.g. prev => [...prev, newItem])
  const newValue = typeof action.payload === 'function'
    ? action.payload(state[key])
    : action.payload;

  // Avoid unnecessary re-renders if the value hasn't changed
  if (state[key] === newValue) {
    return state;
  }

  return { ...state, [key]: newValue };
}

export function AppStateProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // dispatch from useReducer is stable (never changes), so only state
  // is needed in the dependency array. This prevents the context value
  // object reference from changing unless state actually changes.
  const value = useMemo(() => ({ state, dispatch }), [state]);

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within AppStateProvider');
  }
  return context;
}
